import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { OfflineService } from "@phdsoft/common";
import { AnomalyActionModel } from "projects/phdsoft/common/src/lib/models/anomaly-action.model";
import { AnomalyPhotoModel } from "projects/phdsoft/common/src/lib/models/anomaly-photo.model";
import { AnomalyRepairModel } from "projects/phdsoft/common/src/lib/models/anomaly-repair.model";
import { AnomalySketchModel } from "projects/phdsoft/common/src/lib/models/anomaly-sketch.model";
import { AnomalyTypeModel } from "projects/phdsoft/common/src/lib/models/anomaly-type.model";
import { AnomalyModel } from "projects/phdsoft/common/src/lib/models/anomaly.model";
import { OfflineChangesService } from "projects/phdsoft/common/src/lib/shared/services/offline-changes.service";
import { Observable, of } from "rxjs";
import { environment } from "src/environments/environment";
import { ActionCategory } from "../pages/anomaly/anomaly-actions/anomaly-actions.component";

@Injectable({
    providedIn: 'root',
})
export class AnomalyService {

    constructor(
        private http: HttpClient,
        private readonly offlineService: OfflineService,
        private readonly offlineChangesService: OfflineChangesService
    ) {

    }

    //#region Anomaly Types
    getAnomalyTypes(): Observable<AnomalyTypeModel[]> {
        return this.http.get<AnomalyTypeModel[]>(
            environment.API_INSPEC + 'AnomalyType/getall',
        )
    }


    update(anomalyType: AnomalyTypeModel) {
        return this.http.put<boolean>(
            environment.API_INSPEC + 'AnomalyType', anomalyType
        )
    }

    create(anomalyType: AnomalyTypeModel) {
        return this.http.post<AnomalyTypeModel>(
            environment.API_INSPEC + 'AnomalyType', anomalyType
        )
    }

    delete(id: string) {
        return this.http.delete<boolean>(
            environment.API_INSPEC + `AnomalyType?id=${id}`
        )
    }
    //#endregion

    //#region Anomaly Action
    getAnomalyActions() {
        return this.http.get<AnomalyActionModel[]>(
            environment.API_INSPEC + 'AnomalyAction/GetAll',
        )
    }

    getAnomalyActionsByAssetId(assetId: string) {
        return this.http.get<AnomalyActionModel[]>(
            environment.API_INSPEC + `AnomalyAction/GetAllByAssetId?assetId=${assetId}`,
        )
    }

    updateAction(anomalyAction: AnomalyActionModel) {
        return this.http.put<boolean>(
            environment.API_INSPEC + 'AnomalyAction', anomalyAction
        )
    }

    createAction(anomalyAction: AnomalyActionModel) {
        anomalyAction.actionType = "Mitigation"
        return this.http.post<AnomalyActionModel>(
            environment.API_INSPEC + 'AnomalyAction', anomalyAction
        )
    }

    deleteAction(id: string) {
        return this.http.delete<boolean>(
            environment.API_INSPEC + `AnomalyAction?id=${id}`
        )
    }
    //#endregion

    //#region Anomaly Repair
    getAnomalyRepairs() {
        return this.http.get<AnomalyRepairModel[]>(
            environment.API_INSPEC + 'AnomalyRepair/getall',
        )
    }

    getAnomalyRepairsByAssetId(assetId: string) {
        return this.http.get<AnomalyRepairModel[]>(
            environment.API_INSPEC + `AnomalyRepair/GetAllByAssetId?assetId=${assetId}`,
        )
    }

    deleteRepair(id: string) {
        return this.http.delete<boolean>(
            environment.API_INSPEC + `AnomalyRepair?id=${id}`
        )
    }

    createRepair(anomalyRepair: AnomalyRepairModel) {
        anomalyRepair.actionType = "Repair"
        return this.http.post<AnomalyRepairModel>(
            environment.API_INSPEC + 'AnomalyRepair', anomalyRepair
        )
    }

    updateRepair(anomalyRepair: AnomalyRepairModel) {
        return this.http.put<boolean>(
            environment.API_INSPEC + 'AnomalyRepair', anomalyRepair
        )
    }
    //#endregion

    //#region Anomaly Item
    public getAnomalyItem(assetId: string, regionId?: string) {
        const preventCache = (new Date()).getTime();
        let url: string = `Anomaly?assetId=${assetId}`
        if (regionId && regionId != '0') {
            url += `&regionId=${regionId}`
        }
        return this.http.get<AnomalyModel[]>(
            environment.API_INSPEC + url + `&cache_bump=${preventCache}`
        )
    }

    async getAnomalyList(assetId: string, regionId?: string) {
        let anomalies: AnomalyModel[];
        if (window.navigator.onLine) {
            anomalies = await this.getAnomalyItem(assetId, regionId).toPromise()
        }
        else {
            let dbStoreData = await this.offlineService.getItem(`anomalies_${assetId}`)
            anomalies = dbStoreData?.json
        }

        return anomalies;
    }

    getAnomalyListByIds(anomalyIds: string[]) {
        const preventCache = (new Date()).getTime();
        let url: string = `Anomaly/GetByAnomalyIds`
        return this.http.post<AnomalyModel[]>(
            environment.API_INSPEC + url + `?cache_bump=${preventCache}`, anomalyIds
        )
    }

    deleteAnomalyItem(id: string) {
        // TODO: unify anomaly deletion on backend... no need to call so many endpoints here....
        if (window.navigator.onLine) {
            this.getAnomalySketches(id).subscribe(sketches => {
                if (sketches.length > 0) {
                    var sketchesId = []
                    for (const sketch of sketches) {
                        sketchesId.push(sketch.id)
                    }
                    this.deleteAnomalySketch(id, sketchesId)
                }
            })

            return this.http.delete<boolean>(
                environment.API_INSPEC + `Anomaly?id=${id}`
            )
        }
        else {
            this.offlineChangesService.addItem("anomaly", environment.API_INSPEC + `Anomaly?id=${id}`, "delete", null)
            return of(true)
        }
    }

    updateAnomalyItem(anomaly: AnomalyModel) {
      const url = environment.API_INSPEC + 'Anomaly'
      if (window.navigator.onLine) {
        return this.http.put<boolean>(url, anomaly)
      }
      else {
        this.offlineChangesService.addItem("anomaly", url, "put", anomaly)
        return of(true)
      }
    }

    createAnomaly(anomaly: AnomalyModel):  Observable<any>  {
        const url = environment.API_INSPEC + 'Anomaly'
        if (window.navigator.onLine) {
            return this.http.post<AnomalyModel>(url, anomaly)
        }
        else {
            this.offlineChangesService.addItem("anomaly", url, "post", anomaly)
            return of(anomaly)
        }

    }

    //#endregion

    //#region Anomaly Sketches
    getAnomalySketches(anomalyId: string) {
        return this.http.get<AnomalySketchModel[]>(
            environment.API_INSPEC + 'AnomalySketch?anomalyId=' + anomalyId,
        )
    }

    createAnomalySketch(files: FileList, anomalyId: string) {
        let formData = new FormData();
        for (let index = 0; index < files.length; index++) {
            let element = files[index];
            formData.append("pFormFiles", element)
        }

        return this.http.post(
            environment.API_INSPEC + 'AnomalySketch?anomalyId=' + anomalyId, formData
        )
    }

    deleteAnomalySketch(anomalyId: string, sketchIds: string[]) {
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            }),
            body: {
                anomalyId: anomalyId,
                Ids: sketchIds
            },
        };

        return this.http.delete(
            environment.API_INSPEC + `AnomalySketch`, options
        )
    }
    //#endregion

    downloadFile(path: string, anomalyId: string) {
        return this.http.get(
            environment.API_INSPEC + `Anomaly/getFile?path=${path}&anomalyId=${anomalyId}`, {
            responseType: 'blob'
        }
        )
    }

    getActionCategories(){
        return this.http.get<ActionCategory[]>(
            environment.API_INSPEC + 'AnomalyAction/getCategories',
        )
    }
}
